import { useState, useEffect } from 'react';
import { studentService, purchaseService, rewardService, attendanceService, courseService } from '../services';

/**
 * 上课记录表单的共享业务逻辑
 * @param {Object} initialData - 初始数据（编辑模式使用）
 * @returns {Object} 表单状态和方法
 */
export const useRecordForm = (initialData = {}) => {
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(
    initialData.student_id ? String(initialData.student_id) : ''
  );
  const [selectedCourseId, setSelectedCourseId] = useState(
    initialData.course_id ? String(initialData.course_id) : ''
  );
  const [remainingHours, setRemainingHours] = useState(initialData.remaining_hours || 0);
  const [giftBalance, setGiftBalance] = useState(0);
  const [loading, setLoading] = useState(false);

  // 获取今天的日期（格式：YYYY-MM-DD）
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const [rawStudents, setRawStudents] = useState([]);
  const [studentAttendances, setStudentAttendances] = useState({});
  const [selectedDate, setSelectedDate] = useState(initialData.date || getTodayDate());

  // 加载原始学生列表和考勤记录
  useEffect(() => {
    const loadData = async () => {
      try {
        const [response, attendanceRes] = await Promise.all([
          studentService.getList({ page: 1, per_page: 1000 }),
          attendanceService.getList({ page: 1, per_page: 1000 })
        ]);
        
        // 提取每个学生最近所有的上课日期
        const tempMap = {};
        if (attendanceRes && attendanceRes.items) {
          attendanceRes.items.forEach(record => {
            if (!tempMap[record.student_id]) {
              tempMap[record.student_id] = [];
            }
            // 只保留最近 50 次上课记录（大约一年的记录），防止数组过大
            if (tempMap[record.student_id].length < 50) {
              tempMap[record.student_id].push(record.date);
            }
          });
        }
        setStudentAttendances(tempMap);

        // 只保留student_type为"在学"的学生
        const activeStudents = (response.items || []).filter(
          student => student.student_type === '在学'
        );
        setRawStudents(activeStudents);
      } catch (error) {
        console.error('获取学生数据失败:', error);
      }
    };
    loadData();
  }, []);

  // 解析 YYYY-MM-DD 获取星期几 (0是周日)
  const getDayOfWeek = (dateString) => {
    if (!dateString) return -1;
    const match = dateString.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return -1;
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const day = parseInt(match[3], 10);
    return new Date(year, month - 1, day).getDay();
  };

  // 根据选择的日期动态排序学生
  useEffect(() => {
    if (rawStudents.length === 0) return;

    const targetDayOfWeek = getDayOfWeek(selectedDate);
    
    let matchCount = 0;
    const sorted = [...rawStudents].sort((a, b) => {
      const aRecords = studentAttendances[a.id] || [];
      const bRecords = studentAttendances[b.id] || [];
      
      // 判断该学生在最近的记录中，是否有过在 targetDayOfWeek 上课的记录
      const aMatches = aRecords.some(date => getDayOfWeek(date) === targetDayOfWeek);
      const bMatches = bRecords.some(date => getDayOfWeek(date) === targetDayOfWeek);
      
      if (aMatches && !bMatches) return -1;
      if (!aMatches && bMatches) return 1;
      
      // 如果都匹配，或者都不匹配，则按最后一次上课的绝对时间倒序排列（越近期越靠前）
      const aLastDate = aRecords.length > 0 ? aRecords[0] : '1970-01-01';
      const bLastDate = bRecords.length > 0 ? bRecords[0] : '1970-01-01';
      
      if (aLastDate > bLastDate) return -1;
      if (aLastDate < bLastDate) return 1;
      
      return 0;
    });
    
    sorted.forEach(s => {
       const sRecords = studentAttendances[s.id] || [];
       if (sRecords.some(date => getDayOfWeek(date) === targetDayOfWeek)) {
           matchCount++;
       }
    });
    
    console.log(`[useRecordForm] Date changed to: ${selectedDate}, target DOW: ${targetDayOfWeek}. Found ${matchCount} matching students. Total students: ${sorted.length}`);

    setStudents(sorted);
  }, [rawStudents, studentAttendances, selectedDate]);

  // 根据学生ID加载课程列表（包含活跃课程和最近一年上过课的课程）
  useEffect(() => {
    const loadCourses = async () => {
      if (!selectedStudentId) {
        setCourses([]);
        return;
      }
      
      try {
        setLoading(true);
        console.log('[useRecordForm] 开始加载课程，学生ID:', selectedStudentId);
        
        // 1. 获取购课记录中的活跃课程
        const purchaseRes = await purchaseService.getList({ 
          student_id: selectedStudentId,
          page: 1, 
          per_page: 1000 
        });
        
        // 提取唯一的课程
        const uniqueCourses = [];
        const courseIds = new Set();
        
        // 只展示当前学生还有课时的课程清单
        (purchaseRes.items || []).forEach(record => {
          if (record.course_id && !courseIds.has(record.course_id) && record.remaining_hours > 0) {
            courseIds.add(record.course_id);
            const courseName = (record.course_name || '未知课程').split(' | ')[0];
            uniqueCourses.push({
              id: record.course_id,
              name: courseName,
              source: 'purchase'
            });
          }
        });
        
        console.log('[useRecordForm] 组合后的课程列表:', uniqueCourses);
        setCourses(uniqueCourses);
      } catch (error) {
        console.error('[useRecordForm] 获取课程列表失败:', error);
        setCourses([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadCourses();
  }, [selectedStudentId]);

  // 加载所有可用课程
  const [allCourses, setAllCourses] = useState([]);
  useEffect(() => {
    const loadAllCourses = async () => {
      try {
        const res = await courseService.getNameList();
        // 如果后端返回的是 { items: [...] } 或者直接就是数组
        setAllCourses(res.items || res || []);
      } catch (error) {
        console.error('获取所有课程列表失败:', error);
        setAllCourses([]);
      }
    };
    loadAllCourses();
  }, []);

  // 根据学生ID和课程ID获取剩余课时
  useEffect(() => {
    const loadRemainingHours = async () => {
      if (!selectedStudentId || !selectedCourseId) {
        setRemainingHours(0);
        return;
      }
      
      try {
        setLoading(true);
        // 获取该学生该课程的购课记录
        const response = await purchaseService.getList({ 
          student_id: selectedStudentId,
          course_id: selectedCourseId,
          page: 1, 
          per_page: 1 
        });
        
        if (response.items && response.items.length > 0) {
          // 取最新的购课记录的剩余课时
          setRemainingHours(response.items[0].remaining_hours || 0);
        } else {
          setRemainingHours(0);
        }
      } catch (error) {
        console.error('获取剩余课时失败:', error);
        setRemainingHours(0);
      } finally {
        setLoading(false);
      }
    };
    
    loadRemainingHours();
  }, [selectedStudentId, selectedCourseId]);

  // 加载学生赠送余额
  useEffect(() => {
    const loadGiftBalance = async () => {
      if (!selectedStudentId) {
        setGiftBalance(0);
        return;
      }
      try {
        const res = await rewardService.getBalance(selectedStudentId);
        setGiftBalance(res.available_reward || 0);
      } catch (error) {
        console.error('获取可用赠送余额失败:', error);
        setGiftBalance(0);
      }
    };
    loadGiftBalance();
  }, [selectedStudentId]);

  // 处理学生选择变化
  const handleStudentChange = async (studentId) => {
    setSelectedStudentId(studentId);
    setRemainingHours(0);
    
    if (!studentId) {
      setSelectedCourseId('');
      return;
    }

    try {
      // 尝试获取该学员最近一次的上课记录，作为默认课程
      const lastSession = await attendanceService.getList({
        student_id: studentId,
        page: 1,
        per_page: 1
      });

      if (lastSession.items && lastSession.items.length > 0) {
        const lastCourseId = String(lastSession.items[0].course_id);
        console.log('[useRecordForm] 自动匹配上一次课程 ID:', lastCourseId);
        setSelectedCourseId(lastCourseId);
      } else {
        setSelectedCourseId('');
      }
    } catch (err) {
      console.warn('[useRecordForm] 获取最近课程失败:', err);
      setSelectedCourseId('');
    }
  };

  // 处理课程选择变化
  const handleCourseChange = (courseId) => {
    setSelectedCourseId(courseId);
  };

  return {
    students,
    courses,
    allCourses,
    selectedStudentId,
    selectedCourseId,
    remainingHours,
    giftBalance,
    loading,
    selectedDate,
    setSelectedDate,
    getTodayDate,
    handleStudentChange,
    handleCourseChange,
  };
};
